import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  FormError,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Status,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  formatDate,
  formatRelative,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@lawbee/ui';
import { Briefcase, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage, fieldErrors, isApiError, qs } from '../../../lib/api';
import { todayIn } from '../../../lib/hooks';
import { useMe } from '../../../lib/me';
import type { MatterSummary } from '../../../lib/types';
import { useDebounced, useMatterTypes, useTeam } from './shared';

type StatusFilter = 'open' | 'closed' | 'all';

export function MattersPage() {
  const me = useMe();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['matters', status, q],
    queryFn: () => api.get<{ matters: MatterSummary[] }>(`/api/matters${qs({ status, q })}`),
    placeholderData: (prev) => prev,
  });
  const matters = list.data?.matters ?? [];
  const today = todayIn(me.workspace.timezone);
  const open = (id: string) => void navigate({ to: '/matters/$id', params: { id } });

  return (
    <div>
      <PageHeader
        title="Matters"
        description="Every open file, its stage and what is due next."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden /> New matter
          </Button>
        }
      />
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div role="tablist" aria-label="Filter by status" className={segmentedListClass}>
          {(
            [
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All' },
            ] as const
          ).map((f) => (
            <button key={f.value} type="button" role="tab" aria-selected={status === f.value} className={segmentedItemClass(status === f.value)} onClick={() => setStatus(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input type="search" aria-label="Search matters" placeholder="Search number, title or client" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      {list.isLoading ? (
        <LoadingState label="Loading matters" />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : matters.length === 0 ? (
        <EmptyState
          icon={<Briefcase />}
          title={q ? 'No matters match your search' : status === 'closed' ? 'No closed matters' : 'No matters yet'}
          description={q ? 'Try a different name or matter number.' : 'Open a matter directly, or convert a cleared intake.'}
          action={
            q ? undefined : (
              <Button onClick={() => setCreating(true)}>
                <Plus aria-hidden /> New matter
              </Button>
            )
          }
        />
      ) : (
        <>
          <ul className="space-y-2 md:hidden">
            {matters.map((m) => (
              <li key={m.id}>
                <Link to="/matters/$id" params={{ id: m.id }} className="block rounded-xl bg-card p-4 shadow-card transition-colors hover:bg-muted/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="figures text-[12px] text-muted-foreground">{m.number}</p>
                      <p className="truncate font-medium">{m.title}</p>
                      <p className="truncate text-[13px] text-muted-foreground">{m.clientName ?? 'No client'}</p>
                    </div>
                    <StageStatus matter={m} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
                    {m.nextDeadline ? (
                      <span className={m.nextDeadline < today ? 'text-destructive' : undefined}>
                        Next deadline <span className="figures">{formatDate(m.nextDeadline)}</span>
                      </span>
                    ) : null}
                    <span className="figures ml-auto">{formatRelative(m.lastActivityAt ?? m.openedAt)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-hidden rounded-xl bg-card shadow-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Responsible</TableHead>
                  <TableHead>Next deadline</TableHead>
                  <TableHead className="text-right">Last activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => open(m.id)}>
                    <TableCell className="figures whitespace-nowrap">{m.number}</TableCell>
                    <TableCell className="max-w-72">
                      <Link to="/matters/$id" params={{ id: m.id }} className="line-clamp-2 font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                        {m.title}
                      </Link>
                    </TableCell>
                    <TableCell>{m.clientName ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{m.matterTypeName ?? '—'}</TableCell>
                    <TableCell>
                      <StageStatus matter={m} />
                    </TableCell>
                    <TableCell>{m.responsibleName ?? '—'}</TableCell>
                    <TableCell className={m.nextDeadline && m.nextDeadline < today ? 'figures whitespace-nowrap text-destructive' : 'figures whitespace-nowrap'}>
                      {m.nextDeadline ? formatDate(m.nextDeadline) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="figures whitespace-nowrap text-right text-muted-foreground">{formatRelative(m.lastActivityAt ?? m.openedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <NewMatterDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function StageStatus({ matter }: { matter: MatterSummary }) {
  if (matter.status === 'closed') {
    return (
      <Status tone="idle" className="text-muted-foreground">
        Closed
      </Status>
    );
  }
  return <Status tone="progress">{matter.stageLabel || 'No stage'}</Status>;
}

const EMPTY = { title: '', matterTypeId: '', firstName: '', lastName: '', email: '', phone: '', responsibleUserId: '', description: '' };

export function NewMatterDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const types = useMatterTypes();
  const team = useTeam();
  const [v, setV] = useState({ ...EMPTY, responsibleUserId: me.user.id });
  const set = (patch: Partial<typeof v>) => setV((prev) => ({ ...prev, ...patch }));
  const activeTypes = (types.data?.types ?? []).filter((t) => t.active);

  useEffect(() => {
    if (open && !v.matterTypeId && activeTypes[0]) set({ matterTypeId: activeTypes[0].id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTypes.length]);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ matter: { id: string } }>('/api/matters', {
        title: v.title.trim(),
        matterTypeId: v.matterTypeId,
        client: { firstName: v.firstName.trim(), lastName: v.lastName.trim(), email: v.email.trim() || null, phone: v.phone.trim() || null },
        responsibleUserId: v.responsibleUserId || null,
        description: v.description.trim() || null,
      }),
    onSuccess: async (res) => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['matters'] }), qc.invalidateQueries({ queryKey: ['dashboard'] })]);
      toast.success('Matter opened');
      onOpenChange(false);
      setV({ ...EMPTY, responsibleUserId: me.user.id });
      void navigate({ to: '/matters/$id', params: { id: res.matter.id } });
    },
  });
  const errs = fieldErrors(create.error);
  const limit = isApiError(create.error, 'limit_reached');
  const valid = v.title.trim().length >= 2 && !!v.matterTypeId && !!v.firstName.trim() && !!v.lastName.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) create.reset();
      }}
    >
      <DialogContent
        size="lg"
        title="New matter"
        description="The client is added to your contacts."
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" form="new-matter" loading={create.isPending} disabled={!valid}>
              Open matter
            </Button>
          </>
        }
      >
        <form
          id="new-matter"
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) create.mutate();
          }}
        >
          {limit ? (
            <Alert variant="warning" title="Open matter limit reached">
              {errorMessage(create.error)}{' '}
              <Link to="/settings/billing" className="font-medium text-foreground underline-offset-4 hover:underline">
                See plans
              </Link>
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Matter title" htmlFor="nm-title" required className="sm:col-span-2" error={errs.title}>
              <Input id="nm-title" value={v.title} onChange={(e) => set({ title: e.target.value })} placeholder="Smith - separation agreement" />
            </Field>
            <Field label="Matter type" htmlFor="nm-type" required error={errs.matterTypeId}>
              <Select
                id="nm-type"
                value={v.matterTypeId}
                onChange={(e) => set({ matterTypeId: e.target.value })}
                placeholder={types.isLoading ? 'Loading…' : activeTypes.length ? 'Choose…' : 'No matter types set up'}
                options={activeTypes.map((t) => ({ value: t.id, label: t.name }))}
              />
            </Field>
            <Field label="Responsible lawyer" htmlFor="nm-resp">
              <Select
                id="nm-resp"
                value={v.responsibleUserId}
                onChange={(e) => set({ responsibleUserId: e.target.value })}
                options={(team.data?.members ?? []).map((m) => ({ value: m.userId, label: m.name || m.email }))}
                placeholder={team.isLoading ? 'Loading…' : 'Unassigned'}
              />
            </Field>
          </div>
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">Client</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" htmlFor="nm-first" required error={errs['client.firstName']}>
                <Input id="nm-first" autoComplete="off" value={v.firstName} onChange={(e) => set({ firstName: e.target.value })} />
              </Field>
              <Field label="Last name" htmlFor="nm-last" required error={errs['client.lastName']}>
                <Input id="nm-last" autoComplete="off" value={v.lastName} onChange={(e) => set({ lastName: e.target.value })} />
              </Field>
              <Field label="Email" htmlFor="nm-email" error={errs['client.email']}>
                <Input id="nm-email" type="email" autoComplete="off" value={v.email} onChange={(e) => set({ email: e.target.value })} />
              </Field>
              <Field label="Phone" htmlFor="nm-phone" error={errs['client.phone']}>
                <Input id="nm-phone" type="tel" autoComplete="off" value={v.phone} onChange={(e) => set({ phone: e.target.value })} />
              </Field>
            </div>
          </fieldset>
          <Field label="Description" htmlFor="nm-desc">
            <Textarea id="nm-desc" rows={3} value={v.description} onChange={(e) => set({ description: e.target.value })} />
          </Field>
          {create.error && !limit ? <FormError message={errorMessage(create.error)} /> : null}
          {types.error ? <FormError message={errorMessage(types.error)} /> : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
